import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { passwordError } from "../lib/passwordPolicy";
import { PasswordRules } from "../components/PasswordRules";
import { motion } from "framer-motion";
import { useTaxonomy } from "../hooks/useTaxonomy";
import TaxonomyManager from "../components/TaxonomyManager";
import axios from "axios";
import Layout from "../components/Layout";
import { 
  User, Mail, MapPin, Award, Target, Briefcase, 
  Save, MessageCircle, CheckCircle, Clock, Bell, BellOff, KeyRound, Loader2
} from "lucide-react";

const EMAIL_PREFS = [
  { key: "new_messages", label: "New Messages", desc: "When someone sends you a private message" },
  { key: "discussion_replies", label: "Discussion Replies", desc: "When someone replies to your discussion" },
  { key: "post_moderation", label: "Post Moderation", desc: "When your post is flagged or requires review" },
  { key: "security_alerts", label: "Security Alerts", desc: "Account security warnings and strike notices" },
  { key: "community_updates", label: "Community Updates", desc: "Monthly digest and community announcements" },
  { key: "job_alerts", label: "Job Alerts", desc: "New job postings matching your province" },
  { key: "weekly_digest", label: "Weekly 'For You' Digest", desc: "Personalized weekly round-up: top mentor matches, meetups, hot discussions, unread chats" },
];

const ProfilePage = () => {
  const { userId } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    province: "",
    role: "",
    roles: [],
    qualifications: [],
    goals: ""
  });
  const [emailPrefs, setEmailPrefs] = useState({
    new_messages: true,
    discussion_replies: true,
    post_moderation: true,
    security_alerts: true,
    community_updates: true,
    job_alerts: false,
    weekly_digest: true,
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [searchParams] = useSearchParams();
  const passwordSectionRef = useRef(null);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const isOwnProfile = !userId || userId === user?.user_id;
  const targetUserId = userId || user?.user_id;

  const { items: qualificationItems } = useTaxonomy("profile_certifications");
  const { items: roleItems } = useTaxonomy("profile_roles");
  const ROLES = roleItems.length > 0
    ? roleItems.map(i => ({ value: i.key, label: i.label }))
    : [
        { value: "member", label: "Member" },
        { value: "owner", label: "Practice Owner" },
        { value: "office_manager", label: "Office Manager" },
        { value: "myofunctional_therapist", label: "Myofunctional Therapist" }
      ];

  const QUALIFICATIONS = qualificationItems.map(i => i.label);

  useEffect(() => {
    fetchProvinces();
    fetchProfile();
    if (isOwnProfile) {
      axios.get(`${API}/users/email-preferences`).then(r => setEmailPrefs(r.data)).catch(() => {});
    }
  }, [targetUserId]);

  const fetchProvinces = async () => {
    try {
      const response = await axios.get(`${API}/provinces`);
      setProvinces(response.data);
    } catch (error) {
      console.error("Failed to fetch provinces");
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/profile/${targetUserId}`);
      setProfile(response.data);
      const initialRoles = Array.isArray(response.data.roles) && response.data.roles.length
        ? response.data.roles
        : (response.data.role ? [response.data.role] : []);
      setFormData({
        name: response.data.name || "",
        province: response.data.province || "",
        role: response.data.role || "member",
        roles: initialRoles,
        qualifications: response.data.qualifications || [],
        goals: response.data.goals || ""
      });
    } catch (error) {
      if (isOwnProfile) {
        setProfile(user);
        const initialRoles = Array.isArray(user?.roles) && user.roles.length
          ? user.roles
          : (user?.role ? [user.role] : []);
        setFormData({
          name: user?.name || "",
          province: user?.province || "",
          role: user?.role || "member",
          roles: initialRoles,
          qualifications: user?.qualifications || [],
          goals: user?.goals || ""
        });
      } else {
        toast.error("Profile not found");
        navigate("/members");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        roles: formData.roles && formData.roles.length ? formData.roles : ["member"],
        role: (formData.roles && formData.roles[0]) || formData.role || "member",
      };
      await axios.put(`${API}/users/profile`, payload);
      await refreshUser();
      setProfile({ ...profile, ...payload });
      setEditing(false);
      toast.success("Profile updated!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (roleValue) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleValue)
        ? prev.roles.filter(r => r !== roleValue)
        : [...prev.roles, roleValue]
    }));
  };

  const handleTogglePref = async (key) => {
    const updated = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(updated);
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/users/email-preferences`, updated);
    } catch {
      setEmailPrefs(emailPrefs); // revert
      toast.error("Failed to save preference");
    } finally {
      setSavingPrefs(false);
    }
  };

  const toggleQualification = (qual) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.includes(qual)
        ? prev.qualifications.filter(q => q !== qual)
        : [...prev.qualifications, qual]
    }));
  };

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.next) {
      toast.error("Please fill in both current and new password");
      return;
    }
    const pwErr = passwordError(pwForm.next);
    if (pwErr) {
      toast.error(pwErr);
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.next,
      });
      toast.success("Password updated. You've been signed out on all other devices.");
      setPwForm({ current: "", next: "", confirm: "" });
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password");
    } finally {
      setPwSaving(false);
    }
  };

  // Auto-scroll + focus the password section if URL has ?tab=password
  useEffect(() => {
    if (!loading && isOwnProfile && searchParams.get("tab") === "password" && passwordSectionRef.current) {
      passwordSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, isOwnProfile, searchParams]);

  const getVerificationBadge = () => {
    const status = profile?.verification_status;
    if (status === "verified") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#10B981] text-white">
          <CheckCircle className="w-4 h-4 mr-1" />
          Verified
        </span>
      );
    }
    if (status === "submitted") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#8DB5A0] text-white">
          <Clock className="w-4 h-4 mr-1" />
          Under Review
        </span>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-8 lg:p-12">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-[#F0ECF7] rounded-2xl"></div>
            <div className="h-64 bg-[#F0ECF7] rounded-2xl"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12 max-w-4xl" data-testid="profile-page">
        {/* Header Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#E5DFEC] rounded-2xl p-6 md:p-8 mb-6"
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-24 h-24 rounded-2xl bg-[#8DB5A0] text-white flex items-center justify-center text-3xl font-medium shrink-0">
              {profile?.picture ? (
                <img src={profile.picture} alt="" className="w-full h-full rounded-2xl object-cover" />
              ) : (
                profile?.name?.[0] || 'U'
              )}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                {editing ? (
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="text-2xl font-medium bg-white border-[#E5DFEC]"
                    data-testid="edit-name-input"
                  />
                ) : (
                  <h1 className="text-2xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {profile?.name}
                  </h1>
                )}
                {getVerificationBadge()}
                {profile?.is_beta_tester && !profile?.lost_beta_lock && (
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#6B4E8A] text-white"
                    data-testid="beta-founding-badge"
                    title={
                      profile?.beta_locked_price_cad
                        ? `Beta Founding Member · ${profile?.beta_locked_tier === 'pro' ? 'RDH Pro ' : ''}$${profile.beta_locked_price_cad}/${profile.beta_locked_interval === 'year' ? 'yr' : 'mo'} locked for life`
                        : "Beta Founding Member · Your tier's price is locked for life"
                    }
                  >
                    <Award className="w-3 h-3" />
                    {profile?.beta_locked_price_cad
                      ? `Beta Founding · $${profile.beta_locked_price_cad}/${profile.beta_locked_interval === 'year' ? 'yr' : 'mo'} locked`
                      : "Beta Founding · Price locked"}
                  </span>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-[#64748B] mb-4">
                {profile?.email && (
                  <span className="flex items-center gap-1 text-sm">
                    <Mail className="w-4 h-4" />
                    {profile.email}
                  </span>
                )}
                {(profile?.province || editing) && (
                  editing ? (
                    <Select
                      value={formData.province}
                      onValueChange={(value) => setFormData({ ...formData, province: value })}
                    >
                      <SelectTrigger className="w-[180px] bg-white border-[#E5DFEC]">
                        <SelectValue placeholder="Select province" />
                      </SelectTrigger>
                      <SelectContent>
                        {provinces.map((p) => (
                          <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="flex items-center gap-1 text-sm">
                      <MapPin className="w-4 h-4" />
                      {provinces.find(p => p.code === profile?.province)?.name || profile?.province}
                    </span>
                  )
                )}
              </div>

              {isOwnProfile && (
                <div className="flex gap-3">
                  {editing ? (
                    <>
                      <Button
                        onClick={handleSave}
                        className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
                        disabled={saving}
                        data-testid="save-profile-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditing(false);
                          setFormData({
                            name: profile?.name || "",
                            province: profile?.province || "",
                            role: profile?.role || "member",
                            roles: (Array.isArray(profile?.roles) && profile.roles.length)
                              ? profile.roles
                              : (profile?.role ? [profile.role] : []),
                            qualifications: profile?.qualifications || [],
                            goals: profile?.goals || ""
                          });
                        }}
                        className="rounded-full"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => setEditing(true)}
                      className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full"
                      data-testid="edit-profile-btn"
                    >
                      Edit Profile
                    </Button>
                  )}
                </div>
              )}

              {!isOwnProfile && (
                <Button
                  onClick={() => navigate(`/messages/${profile?.user_id}`)}
                  className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
                  data-testid="message-user-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Role Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-[#E5DFEC] rounded-2xl p-6 md:p-8 mb-6"
        >
          <h2 className="text-lg font-medium text-[#6B4E8A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Briefcase className="w-5 h-5 text-[#8DB5A0]" />
            Professional Role{editing ? "s" : (profile?.roles?.length > 1 ? "s" : "")}
            <TaxonomyManager
              kind="profile_roles"
              title="Professional Role dropdown"
              description="Edit the roles members can pick on their profile."
              compact
            />
          </h2>
          {editing ? (
            <div className="flex flex-wrap gap-2" data-testid="edit-role-multiselect">
              {ROLES.map((role) => {
                const selected = formData.roles.includes(role.value);
                return (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => toggleRole(role.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selected
                        ? 'bg-[#6B4E8A] text-white'
                        : 'bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#E5DFEC]'
                    }`}
                    data-testid={`role-option-${role.value}`}
                  >
                    {role.label}
                  </button>
                );
              })}
            </div>
          ) : (
            (() => {
              const displayRoles = Array.isArray(profile?.roles) && profile.roles.length
                ? profile.roles
                : (profile?.role ? [profile.role] : []);
              return displayRoles.length ? (
                <div className="flex flex-wrap gap-2">
                  {displayRoles.map((r) => (
                    <span key={r} className="px-4 py-2 bg-[#F0ECF7] text-[#6B4E8A] rounded-full font-medium">
                      {ROLES.find(opt => opt.value === r)?.label || r}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="px-4 py-2 bg-[#F0ECF7] text-[#6B4E8A] rounded-full font-medium">Member</span>
              );
            })()
          )}
        </motion.div>

        {/* Qualifications Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-[#E5DFEC] rounded-2xl p-6 md:p-8 mb-6"
        >
          <h2 className="text-lg font-medium text-[#6B4E8A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Award className="w-5 h-5 text-[#8DB5A0]" />
            Qualifications & Certifications
            <TaxonomyManager
              kind="profile_certifications"
              title="Certifications/Credentials"
              description="Edit the list members can pick from on their profile."
              compact
            />
          </h2>
          {editing ? (
            <div className="flex flex-wrap gap-2">
              {QUALIFICATIONS.map((qual) => (
                <button
                  key={qual}
                  type="button"
                  onClick={() => toggleQualification(qual)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    formData.qualifications.includes(qual)
                      ? 'bg-[#6B4E8A] text-white'
                      : 'bg-[#F0ECF7] text-[#6B4E8A] hover:bg-[#E5DFEC]'
                  }`}
                >
                  {qual}
                </button>
              ))}
            </div>
          ) : profile?.qualifications?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {profile.qualifications.map((qual) => (
                <span key={qual} className="px-4 py-2 bg-[#F0ECF7] text-[#6B4E8A] rounded-full text-sm">
                  {qual}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[#64748B]">No qualifications added yet.</p>
          )}
        </motion.div>

        {/* Goals Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-[#E5DFEC] rounded-2xl p-6 md:p-8"
        >
          <h2 className="text-lg font-medium text-[#6B4E8A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Target className="w-5 h-5 text-[#8DB5A0]" />
            Professional Goals
          </h2>
          {editing ? (
            <Textarea
              value={formData.goals}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              placeholder="What are your professional goals?"
              className="bg-white border-[#E5DFEC] focus:border-[#6B4E8A] min-h-[100px]"
              data-testid="edit-goals-input"
            />
          ) : profile?.goals ? (
            <p className="text-[#475569] leading-relaxed">{profile.goals}</p>
          ) : (
            <p className="text-[#64748B]">No goals added yet.</p>
          )}
        </motion.div>

        {/* Change Password (own profile only) */}
        {isOwnProfile && (
          <motion.div
            ref={passwordSectionRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className={`bg-white border rounded-2xl p-6 md:p-8 mb-6 ${
              user?.must_change_password ? "border-[#FCA5A5] ring-2 ring-[#FECACA]" : "border-[#E5DFEC]"
            }`}
            data-testid="password-change-section"
          >
            <h2 className="text-lg font-medium text-[#6B4E8A] mb-1 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <KeyRound className="w-5 h-5 text-[#8DB5A0]" />
              Change Password
            </h2>
            {user?.must_change_password ? (
              <p className="text-sm text-[#DC2626] mb-4">
                {user.must_change_password_reason === "policy"
                  ? "Your current password doesn't meet our new security rules. Please choose a stronger one to keep using DHDH."
                  : "An administrator issued you a temporary password. Please choose a new one now to clear this notice."}
              </p>
            ) : (
              <p className="text-sm text-[#64748B] mb-4">
                Update your account password: at least 10 characters with uppercase, lowercase, a number and a special character. Changing it signs you out everywhere else.
              </p>
            )}
            <div className="grid gap-3 max-w-md">
              <div>
                <Label htmlFor="pw-current" className="text-xs text-[#475569]">Current password</Label>
                <Input
                  id="pw-current"
                  type="password"
                  autoComplete="current-password"
                  value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                  className="bg-white border-[#E5DFEC]"
                  data-testid="pw-current-input"
                />
              </div>
              <div>
                <Label htmlFor="pw-next" className="text-xs text-[#475569]">New password</Label>
                <Input
                  id="pw-next"
                  type="password"
                  autoComplete="new-password"
                  value={pwForm.next}
                  onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                  className="bg-white border-[#E5DFEC]"
                  data-testid="pw-new-input"
                />
                <PasswordRules password={pwForm.next} testId="pw-new-rules" />
              </div>
              <div>
                <Label htmlFor="pw-confirm" className="text-xs text-[#475569]">Confirm new password</Label>
                <Input
                  id="pw-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={pwForm.confirm}
                  onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                  className="bg-white border-[#E5DFEC]"
                  data-testid="pw-confirm-input"
                />
              </div>
              <div>
                <Button
                  onClick={handleChangePassword}
                  disabled={pwSaving}
                  className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full px-5"
                  data-testid="pw-submit-btn"
                >
                  {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Email Notification Preferences */}
        {isOwnProfile && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-[#E5DFEC] rounded-2xl p-6 md:p-8"
            data-testid="email-preferences-section"
          >
            <h2 className="text-lg font-medium text-[#6B4E8A] mb-1 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Bell className="w-5 h-5 text-[#8DB5A0]" />
              Email Notifications
            </h2>
            <p className="text-sm text-[#64748B] mb-5">Choose which email notifications you'd like to receive</p>
            <div className="space-y-1">
              {EMAIL_PREFS.map((pref) => (
                <div key={pref.key} className="flex items-center justify-between py-3 border-b border-[#F0ECF7] last:border-0">
                  <div className="flex-1 mr-4">
                    <p className="text-sm font-medium text-[#6B4E8A]">{pref.label}</p>
                    <p className="text-xs text-[#64748B]">{pref.desc}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={emailPrefs[pref.key]}
                    onClick={() => handleTogglePref(pref.key)}
                    disabled={savingPrefs}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                      emailPrefs[pref.key] ? 'bg-[#10B981]' : 'bg-[#E5DFEC]'
                    }`}
                    data-testid={`email-pref-${pref.key}`}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                      emailPrefs[pref.key] ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </Layout>
  );
};

export default ProfilePage;
